import { TournamentEvent } from './eventUtils';

interface MatchData {
  id: number;
  teams: string;
  opponent: string;
  date: string;
  time: string;
  court: string;
  result?: string | null;
  status: 'on-time' | 'delayed' | 'early';
  isUpcoming: boolean;
  sets?: Array<{ userGames: number; opponentGames: number }>;
  totalGamesFor?: number;
  totalGamesAgainst?: number;
  gamesFor?: number;
  gamesAgainst?: number;
}

interface GroupStanding {
  position: number;
  team: string;
  wins?: number;
  losses?: number;
  gamesFor: number;
  gamesAgainst: number;
  matchesPlayed: number;
  points?: number;
  isUser: boolean;
}

export const generateTournamentData = (tournament: TournamentEvent) => {
  console.log('generateTournamentData - Received tournament:', tournament);
  console.log('generateTournamentData - Tournament format:', tournament.tournamentFormat);
  console.log('generateTournamentData - Tournament status:', tournament.status);
  
  const userTeam = tournament.partner ? `Tú/${tournament.partner}` : 'Tú';
  const nextOpponent = tournament.nextMatch?.opponent || '';
  const nextCourt = tournament.nextMatch?.court || '1';
  const nextTime = tournament.nextMatch?.time || '10:00';
  const isFinished = tournament.status === 'finished';
  
  // Generate consistent opponents based on tournament ID to ensure consistency
  const generateOpponents = (excludeNext: string = '') => {
    const baseOpponents = [
      'López/Martín', 'García/Rodríguez', 'Silva/Torres', 'Ruiz/Fernández',
      'Vega/Morales', 'Castro/Herrera', 'Delgado/Jiménez', 'Romero/Vargas'
    ];
    
    // Convert ID to number for sorting (handle both number and string UUIDs)
    const numericId = typeof tournament.id === 'number' 
      ? tournament.id 
      : tournament.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    // Sort consistently based on tournament ID for deterministic results
    const sortedOpponents = [...baseOpponents].sort((a, b) => 
      (a.charCodeAt(0) + numericId) - (b.charCodeAt(0) + numericId)
    );
    
    // Filter out the next opponent to avoid duplication but keep consistency
    const availableOpponents = sortedOpponents.filter(opp => 
      !excludeNext || (!opp.toLowerCase().includes(excludeNext.toLowerCase().split(' / ')[0]) && 
                       !opp.toLowerCase().includes(excludeNext.toLowerCase().split(' / ')[1]))
    );
    
    return availableOpponents;
  };

  const opponents = generateOpponents(nextOpponent);
  
  console.log('generateTournamentData - Generated opponents:', opponents);
  console.log('generateTournamentData - Tournament format:', tournament.tournamentFormat);

  if (tournament.tournamentFormat === 'round-robin') {
    const result = generateRoundRobinData(tournament, userTeam, nextOpponent, nextCourt, nextTime, opponents, isFinished);
    console.log('generateTournamentData - Generated round-robin result:', result);
    return result;
  } else if (tournament.tournamentFormat === 'eliminacion-directa') {
    return generateEliminationData(tournament, userTeam, nextOpponent, nextCourt, nextTime, opponents, isFinished);
  } else if (tournament.tournamentFormat === 'sumatoria-juegos') {
    return generateSumGamesData(tournament, userTeam, nextOpponent, nextCourt, nextTime, opponents, isFinished);
  }

  // Fallback to round-robin
  const fallbackResult = generateRoundRobinData(tournament, userTeam, nextOpponent, nextCourt, nextTime, opponents, isFinished);
  console.log('generateTournamentData - Generated fallback result:', fallbackResult);
  return fallbackResult;
};

const generateRoundRobinData = (
  tournament: TournamentEvent, 
  userTeam: string, 
  nextOpponent: string, 
  nextCourt: string, 
  nextTime: string,
  opponents: string[],
  isFinished: boolean = false
) => {
  const matches: MatchData[] = [
    {
      id: 1,
      teams: userTeam,
      opponent: opponents[0],
      date: '15 Jul',
      time: '16:00',
      court: '2',
      result: 'Ganaste 2-1',
      status: 'on-time' as const,
      isUpcoming: false,
      sets: [
        { userGames: 6, opponentGames: 4 },
        { userGames: 3, opponentGames: 6 },
        { userGames: 6, opponentGames: 2 }
      ],
      totalGamesFor: 15,
      totalGamesAgainst: 12
    },
    {
      id: 2,
      teams: userTeam,
      opponent: opponents[1],
      date: '16 Jul',
      time: '10:30',
      court: '1',
      result: 'Perdiste 1-2',
      status: 'on-time' as const,
      isUpcoming: false,
      sets: [
        { userGames: 6, opponentGames: 3 },
        { userGames: 4, opponentGames: 6 },
        { userGames: 2, opponentGames: 6 }
      ],
      totalGamesFor: 12,
      totalGamesAgainst: 15
    },
    {
      id: 3,
      teams: userTeam,
      opponent: nextOpponent,
      date: '17 Jul',
      time: nextTime,
      court: nextCourt,
      result: isFinished ? 'Ganaste 2-0' : null,
      status: 'on-time' as const,
      isUpcoming: !isFinished,
      sets: isFinished ? [
        { userGames: 6, opponentGames: 4 },
        { userGames: 6, opponentGames: 3 }
      ] : [],
      totalGamesFor: isFinished ? 12 : 0,
      totalGamesAgainst: isFinished ? 7 : 0
    }
  ];

  const standings: GroupStanding[] = isFinished ? [
    { 
      position: 1, 
      team: userTeam, 
      wins: 3, 
      losses: 0, 
      gamesFor: 39, 
      gamesAgainst: 19, 
      points: 6, 
      matchesPlayed: 3,
      isUser: true 
    },
    { 
      position: 2, 
      team: opponents[1], 
      wins: 2, 
      losses: 1, 
      gamesFor: 33, 
      gamesAgainst: 25, 
      points: 4, 
      matchesPlayed: 3,
      isUser: false 
    },
    { 
      position: 3, 
      team: opponents[0], 
      wins: 1, 
      losses: 2, 
      gamesFor: 24, 
      gamesAgainst: 33, 
      points: 2, 
      matchesPlayed: 3,
      isUser: false 
    },
    { 
      position: 4, 
      team: nextOpponent, 
      wins: 0, 
      losses: 3, 
      gamesFor: 16, 
      gamesAgainst: 32, 
      points: 0, 
      matchesPlayed: 3,
      isUser: false 
    }
  ] : [
    { 
      position: 1, 
      team: opponents[1], 
      wins: 2, 
      losses: 0, 
      gamesFor: 33, 
      gamesAgainst: 18, 
      points: 4, 
      matchesPlayed: 2,
      isUser: false 
    },
    { 
      position: 2, 
      team: userTeam, 
      wins: 1, 
      losses: 1, 
      gamesFor: 27, 
      gamesAgainst: 27, 
      points: 2, 
      matchesPlayed: 2,
      isUser: true 
    },
    { 
      position: 3, 
      team: opponents[0], 
      wins: 1, 
      losses: 1, 
      gamesFor: 24, 
      gamesAgainst: 30, 
      points: 2, 
      matchesPlayed: 2,
      isUser: false 
    },
    { 
      position: 4, 
      team: nextOpponent, 
      wins: 0, 
      losses: 2, 
      gamesFor: 16, 
      gamesAgainst: 25, 
      points: 0, 
      matchesPlayed: 2,
      isUser: false 
    }
  ];

  return { matches, standings };
};

const generateEliminationData = (
  tournament: TournamentEvent, 
  userTeam: string, 
  nextOpponent: string, 
  nextCourt: string, 
  nextTime: string,
  opponents: string[],
  isFinished: boolean = false
) => {
  const matches: MatchData[] = [
    {
      id: 1,
      teams: userTeam,
      opponent: opponents[0],
      date: '20 Jul',
      time: '09:00',
      court: '1',
      result: 'Ganaste 2-0',
      status: 'on-time' as const,
      isUpcoming: false,
      sets: [
        { userGames: 6, opponentGames: 4 },
        { userGames: 6, opponentGames: 2 }
      ],
      totalGamesFor: 12,
      totalGamesAgainst: 6
    },
    {
      id: 2,
      teams: userTeam,
      opponent: nextOpponent,
      date: '21 Jul',
      time: nextTime,
      court: nextCourt,
      result: isFinished ? 'Ganaste 2-1' : null,
      status: 'on-time' as const,
      isUpcoming: !isFinished,
      sets: isFinished ? [
        { userGames: 6, opponentGames: 4 },
        { userGames: 4, opponentGames: 6 },
        { userGames: 6, opponentGames: 3 }
      ] : [],
      totalGamesFor: isFinished ? 16 : 0,
      totalGamesAgainst: isFinished ? 13 : 0
    }
  ];

  // If finished, add final match showing user as champion
  if (isFinished) {
    matches.push({
      id: 3,
      teams: userTeam,
      opponent: "Finalista",
      date: '22 Jul',
      time: '16:00',
      court: '1',
      result: 'CAMPEÓN 2-0',
      status: 'on-time' as const,
      isUpcoming: false,
      sets: [
        { userGames: 6, opponentGames: 2 },
        { userGames: 6, opponentGames: 4 }
      ],
      totalGamesFor: 12,
      totalGamesAgainst: 6
    });
  }

  return { matches, standings: [] };
};

const generateSumGamesData = (
  tournament: TournamentEvent, 
  userTeam: string, 
  nextOpponent: string, 
  nextCourt: string, 
  nextTime: string,
  opponents: string[],
  isFinished: boolean = false
) => {
  const matches: MatchData[] = [
    {
      id: 1,
      teams: userTeam,
      opponent: opponents[0],
      date: '25 Jul',
      time: '16:00',
      court: '2',
      result: 'Ganaste 5-3',
      status: 'on-time' as const,
      isUpcoming: false,
      gamesFor: 5,
      gamesAgainst: 3
    },
    {
      id: 2,
      teams: userTeam,
      opponent: opponents[1],
      date: '26 Jul',
      time: '10:30',
      court: '1',
      result: 'Empataste 4-4',
      status: 'on-time' as const,
      isUpcoming: false,
      gamesFor: 4,
      gamesAgainst: 4
    },
    {
      id: 3,
      teams: userTeam,
      opponent: nextOpponent,
      date: '26 Jul',
      time: nextTime,
      court: nextCourt,
      result: isFinished ? 'Ganaste 6-2' : null,
      status: 'on-time' as const,
      isUpcoming: !isFinished,
      gamesFor: isFinished ? 6 : 0,
      gamesAgainst: isFinished ? 2 : 0
    }
  ];

  // If finished, add more completed matches
  if (isFinished) {
    matches.push(
      {
        id: 4,
        teams: userTeam,
        opponent: opponents[2],
        date: '27 Jul',
        time: '14:00',
        court: '3',
        result: 'Ganaste 5-3',
        status: 'on-time' as const,
        isUpcoming: false,
        gamesFor: 5,
        gamesAgainst: 3
      },
      {
        id: 5,
        teams: userTeam,
        opponent: opponents[3],
        date: '27 Jul',
        time: '16:00',
        court: '2',
        result: 'Empataste 4-4',
        status: 'on-time' as const,
        isUpcoming: false,
        gamesFor: 4,
        gamesAgainst: 4
      }
    );
  }

  const userStats = {
    gamesFor: matches.reduce((total, match) => total + (match.gamesFor || 0), 0),
    gamesAgainst: matches.reduce((total, match) => total + (match.gamesAgainst || 0), 0),
    matchesPlayed: matches.filter(match => !match.isUpcoming).length
  };

  const standings: GroupStanding[] = isFinished ? [
    { 
      position: 1, 
      team: userTeam, 
      gamesFor: userStats.gamesFor, 
      gamesAgainst: userStats.gamesAgainst, 
      matchesPlayed: userStats.matchesPlayed, 
      isUser: true 
    },
    { 
      position: 2, 
      team: opponents[1], 
      gamesFor: 15, 
      gamesAgainst: 8, 
      matchesPlayed: 5, 
      isUser: false 
    },
    { 
      position: 3, 
      team: opponents[0], 
      gamesFor: 12, 
      gamesAgainst: 14, 
      matchesPlayed: 5, 
      isUser: false 
    },
    { 
      position: 4, 
      team: nextOpponent, 
      gamesFor: 8, 
      gamesAgainst: 20, 
      matchesPlayed: 5, 
      isUser: false 
    }
  ] : [
    { 
      position: 1, 
      team: opponents[1], 
      gamesFor: 11, 
      gamesAgainst: 5, 
      matchesPlayed: 2, 
      isUser: false 
    },
    { 
      position: 2, 
      team: userTeam, 
      gamesFor: userStats.gamesFor, 
      gamesAgainst: userStats.gamesAgainst, 
      matchesPlayed: userStats.matchesPlayed, 
      isUser: true 
    },
    { 
      position: 3, 
      team: opponents[0], 
      gamesFor: 8, 
      gamesAgainst: 10, 
      matchesPlayed: 2, 
      isUser: false 
    },
    { 
      position: 4, 
      team: nextOpponent, 
      gamesFor: 6, 
      gamesAgainst: 12, 
      matchesPlayed: 2, 
      isUser: false 
    }
  ];

  return { matches, standings };
};